// src/pages/QuestionBank.js
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio as MuiRadio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';

import api from '../api';                          // ← shared Axios instance with auth-token
import CustomEditor from '../Components/CustomEditor';

import renderMathInElement from 'katex/contrib/auto-render/auto-render';
import 'katex/dist/katex.min.css';

// ─────────────────────────────────────────────────────────────────────────────
// Helper: read-only math renderer
function MathPreview({ html }) {
  const ref = React.useRef(null);
  useEffect(() => {
    if (ref.current) {
      renderMathInElement(ref.current, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$',  right: '$',  display: false },
        ],
        throwOnError: false,
      });
    }
  }, [html]);
  return <Box ref={ref} dangerouslySetInnerHTML={{ __html: html }} />;
}

// Helper: detect duplicate options (case-insensitive)
const hasDuplicateOptions = opts => {
  const norm = opts.map(o => o.trim().toLowerCase());
  return new Set(norm).size !== norm.length;
};
// ─────────────────────────────────────────────────────────────────────────────

function QuestionBank() {
  const navigate = useNavigate();

  // ─── Main-question (parent) form state ────────────────────────────────────
  const [subject, setSubject]           = useState('Mathematics');
  const [level,   setLevel]             = useState(1);
  const [question, setQuestion]         = useState('');
  const [options,  setOptions]          = useState(['', '', '', '']);
  const [correctOptionIndex, setCorrectOptionIndex] = useState(0);
  const [subQuestions,        setSubQuestions]      = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const hasSubQuestions = subQuestions.length > 0;

  // ─── Modal / temp states for adding sub-questions ────────────────────────
  const [subQuestionModalOpen,     setSubQuestionModalOpen]      = useState(false);
  const [viewSubQuestionsModalOpen,setViewSubQuestionsModalOpen] = useState(false);
  const [subQuestionTemp, setSubQuestionTemp] = useState({
    question: '', options: ['', '', '', ''], correctOptionIndex: 0,
  });
  const [subResetCounter, setSubResetCounter] = useState(0);

  // ─── Temp states for adding sub-questions INSIDE the edit dialog ─────────
  const [editSubModalOpen, setEditSubModalOpen] = useState(false);
  const [subQuestionTempEdit, setSubQuestionTempEdit] = useState({
    question: '', options: ['', '', '', ''], correctOptionIndex: 0,
  });
  const [editSubResetCounter, setEditSubResetCounter] = useState(0);

  // ─── Data from server ────────────────────────────────────────────────────
  const [drafts,        setDrafts]        = useState([]);
  const [reviewNeeded,  setReviewNeeded]  = useState([]);
  const [subjectCounts, setSubjectCounts] = useState({});

  // ─── Feedback banners ────────────────────────────────────────────────────
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // ─── View / edit dialog ──────────────────────────────────────────────────
  const [viewOpen,       setViewOpen]       = useState(false);
  const [viewedQuestion, setViewedQuestion] = useState(null);
  const [modalEditMode,  setModalEditMode]  = useState(false);

  const [editQuestionText, setEditQuestionText] = useState('');
  const [editSubQuestions, setEditSubQuestions] = useState([]);

  // Force-remount editors after reset
  const [resetCounter, setResetCounter] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────
  // Initial fetches
  useEffect(() => {
    fetchDrafts();
    fetchReviewNeeded();
    fetchSubjectCounts();
    fetchSessions();
  }, []);

  // ─── Fetch helpers ───────────────────────────────────────────────────────
  const fetchSessions = async () => {
    try {
      const res = await api.get('/api/sessions/active');
      setSessions(res.data);
    } catch {
      setError('Failed to fetch sessions.');
    }
  };
  const fetchDrafts = async () => {
    try {
      const res = await api.get('/api/questions/drafts');
      setDrafts(res.data);
    } catch {
      setError('Failed to fetch draft questions.');
    }
  };

  const fetchReviewNeeded = async () => {
    try {
      const res = await api.get('/api/questions');
      setReviewNeeded(res.data.filter(q => q.status === 'review_needed'));
    } catch {
      setError('Failed to fetch review-needed questions.');
    }
  };

  const fetchSubjectCounts = async () => {
    try {
      const res = await api.get('/api/questions/countBySubject');
      setSubjectCounts(res.data.subjectCounts || {});
    } catch {
      setError('Failed to fetch subject counts.');
    }
  };

  // ─── Form: Save draft ────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!question.trim()) { setError('Question cannot be empty.'); return; }
    if (!hasSubQuestions && options.some(o => !o.trim())) {
      setError('All options must be filled.'); return;
    }
    if (!hasSubQuestions && hasDuplicateOptions(options)) {
      setError('Duplicate options in parent question.'); return;
    }
    setError(''); setSuccess('');
    try {
      await api.post('/api/questions/draft', {
        subject,
        level,
        question,
        options      : hasSubQuestions ? [] : options,
        correctAnswer: hasSubQuestions ? '' : options[correctOptionIndex],
        subQuestions,
        sessionId: sessionId || null,
      });
      setSuccess('Draft question saved!');
      // reset form
      setQuestion('');
      setOptions(['', '', '', '']);
      setCorrectOptionIndex(0);
      setLevel(1);
      setSubQuestions([]);
      setResetCounter(c => c + 1);
      fetchDrafts();
    } catch {
      setError('Failed to create draft question.');
    }
  };

  // ─── Delete draft ────────────────────────────────────────────────────────
  const handleDeleteDraft = async id => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await api.delete(`/api/questions/${id}`);
      setSuccess('Draft question deleted!');
      fetchDrafts();
    } catch {
      setError('Failed to delete draft.');
    }
  };

  // ─── View / Edit dialog helpers ──────────────────────────────────────────
  const handleView = q => {
    setViewedQuestion({ ...q, subQuestions: q.subQuestions || [] });
    setModalEditMode(false);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewedQuestion(null);
    setModalEditMode(false);
  };

  const startEditInModal = () => {
    setModalEditMode(true);
    setEditQuestionText(viewedQuestion.question);
    setEditSubQuestions(viewedQuestion.subQuestions || []);
  };

  const handleSaveModalEdits = async () => {
    if (!editQuestionText.trim()) { setError('Question cannot be empty.'); return; }
    if (!editSubQuestions.length && viewedQuestion.options.some(o => !o.trim())) {
      setError('All options must be filled.'); return;
    }
    if (!editSubQuestions.length && hasDuplicateOptions(viewedQuestion.options)) {
      setError('Duplicate options in parent question.'); return;
    }
    setError(''); setSuccess('');
    try {
      await api.put(`/api/questions/${viewedQuestion._id}`, {
        subject : viewedQuestion.subject,
        level   : viewedQuestion.level || 1,
        question: editQuestionText,
        options : viewedQuestion.options,
        correctAnswer: viewedQuestion.correctAnswer,
        subQuestions : editSubQuestions.length ? editSubQuestions : viewedQuestion.subQuestions,
      });
      setSuccess('Draft question updated!');
      fetchDrafts();
      handleCloseView();
    } catch {
      setError('Failed to update draft question.');
    }
  };

  const handleDeleteInModal = () => {
    if (!viewedQuestion) return;
    handleDeleteDraft(viewedQuestion._id);
    handleCloseView();
  };

  const handleResubmitModal = async () => {
    if (!viewedQuestion) return;
    setError(''); setSuccess('');
    try {
      await api.put(`/api/questions/resubmit/${viewedQuestion._id}`, {
        subject : viewedQuestion.subject,
        level   : viewedQuestion.level || 1,
        question: viewedQuestion.question,
        options : viewedQuestion.options,
        correctAnswer: viewedQuestion.correctAnswer,
        subQuestions : viewedQuestion.subQuestions || [],
      });
      setSuccess('Question resubmitted!');
      fetchDrafts(); fetchReviewNeeded(); handleCloseView();
    } catch {
      setError('Failed to resubmit question.');
    }
  };

  const handleSubmitAll = async () => {
    if (!drafts.length) { setError('No draft questions to submit.'); return; }
    setError(''); setSuccess('');
    try {
      await api.post('/api/questions/submit', { questionIds: drafts.map(d => d._id) });
      setSuccess('All draft questions submitted!');
      fetchDrafts(); fetchSubjectCounts();
    } catch {
      setError('Failed to submit drafts.');
    }
  };

  // ─── Add sub-question temp helpers (main form) ───────────────────────────
  const handleSaveTempSubQuestion = () => {
    if (!subQuestionTemp.question.trim()) { setError('Sub-question text empty.'); return; }
    if (subQuestionTemp.options.some(o => !o.trim())) { setError('Fill all sub-options.'); return; }
    if (hasDuplicateOptions(subQuestionTemp.options)) { setError('Duplicate options in sub-question.'); return; }
    setError('');
    const newSub = {
      question      : subQuestionTemp.question,
      options       : subQuestionTemp.options,
      correctAnswer : subQuestionTemp.options[subQuestionTemp.correctOptionIndex],
    };
    setSubQuestions(prev => [...prev, newSub]);
    setSubQuestionModalOpen(false);
    setSubResetCounter(c => c + 1);
    setSubQuestionTemp({ question: '', options: ['', '', '', ''], correctOptionIndex: 0 });
  };

  // ─── Add sub-question temp helpers (inside edit dialog) ──────────────────
  const handleAddSubQuestionEdit = () => {
    if (!subQuestionTempEdit.question.trim()) { setError('Sub-question text empty.'); return; }
    if (subQuestionTempEdit.options.some(o => !o.trim())) { setError('Fill all sub-options.'); return; }
    if (hasDuplicateOptions(subQuestionTempEdit.options)) { setError('Duplicate options in sub-question.'); return; }
    setError('');
    const newSub = {
      question      : subQuestionTempEdit.question,
      options       : subQuestionTempEdit.options,
      correctAnswer : subQuestionTempEdit.options[subQuestionTempEdit.correctOptionIndex],
    };
    setEditSubQuestions(prev => [...prev, newSub]);
    setEditSubModalOpen(false);
    setEditSubResetCounter(c => c + 1);
    setSubQuestionTempEdit({ question: '', options: ['', '', '', ''], correctOptionIndex: 0 });
  };

  // ─── Logout ──────────────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // palette for subject cards
  const palette = ['#1565c0', '#2e7d32', '#6a1b9a', '#c62828', '#ad1457', '#008394'];

  // =============  JSX ======================================================
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        pb: 3,
        borderBottom: '3px solid',
        borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1',
      }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5 }}>
            Question Bank
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
            Create, manage, and submit your questions
          </Typography>
        </Box>
        <Button 
          variant="outlined"
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.2,
            fontWeight: 600,
            borderWidth: 2,
            borderColor: '#dc3545',
            color: '#dc3545',
            '&:hover': {
              borderWidth: 2,
              borderColor: '#c82333',
              background: '#fff5f5',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Logout
        </Button>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* ─── Subject-wise counts ─────────────────────────────────────────── */}
      <Box sx={{ 
        bgcolor: 'white',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        p: 3,
        mb: 3,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
          Subject-Wise Submitted Questions
        </Typography>
        {Object.keys(subjectCounts).length === 0 ? (
          <Typography sx={{ color: '#7f8c8d' }}>No submitted questions yet.</Typography>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(subjectCounts).map(([sub, count], idx) => (
              <Grid key={sub} item xs={6} sm={4} md={3}>
                <Box sx={{
                  background: `linear-gradient(135deg, ${palette[idx % palette.length]}, ${palette[(idx + 1) % palette.length]})`,
                  color: 'white',
                  borderRadius: 2, 
                  p: 2, 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  },
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{sub}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{count}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* ─── Review-needed banner ─────────────────────────────────────────── */}
      {reviewNeeded.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.2)',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {reviewNeeded.length} Question{reviewNeeded.length > 1 ? 's' : ''} Require{reviewNeeded.length === 1 ? 's' : ''} Your Attention
          </Typography>
          <Typography variant="body2">
            These questions have been sent back for review
          </Typography>
        </Alert>
      )}

      {/* ─── Draft cards ──────────────────────────────────────────────────── */}
      {drafts.length > 0 && (
        <Box sx={{ 
          bgcolor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          p: 3,
          mb: 4,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
              Draft Questions ({drafts.length})
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleSubmitAll}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Submit All Drafts
            </Button>
          </Box>
          
          {drafts.map(dq => (
            <Box key={dq._id} sx={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: 2, 
              p: 2.5, 
              mb: 2,
              bgcolor: '#fafafa',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateX(4px)',
              },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    {dq.subject}
                  </Typography>
                  <Box sx={{
                    bgcolor: '#ff9800', 
                    color: 'white', 
                    px: 1.5, 
                    py: 0.25,
                    borderRadius: 1, 
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}>
                    Draft
                  </Box>
                </Box>
                {dq.dateCreated && (
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
                    Created: {new Date(dq.dateCreated).toLocaleDateString('en-US', { 
                      year: 'numeric', month: 'short', day: 'numeric' 
                    })}
                  </Typography>
                )}
              </Box>

              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'white', 
                borderRadius: 1,
                mb: 1.5,
                border: '1px solid #e0e0e0',
              }}>
                <MathPreview html={dq.question} />
              </Box>

              {dq.subQuestions?.length > 0 && (
                <Box sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: '#e3f2fd',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  mb: 1.5,
                }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#1976d2', fontWeight: 500 }}>
                    {dq.subQuestions.length} sub question{dq.subQuestions.length > 1 ? 's' : ''} added
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={() => handleView(dq)}
                  sx={{ borderRadius: 1.5, fontWeight: 600 }}
                >
                  View
                </Button>
                <Button 
                  size="small" 
                  variant="outlined"
                  color="error" 
                  onClick={() => handleDeleteDraft(dq._id)}
                  sx={{ borderRadius: 1.5, fontWeight: 600 }}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* --------------   NEW QUESTION FORM   ----------------------------- */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Box sx={{ 
        bgcolor: 'white',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
      }}>
        {/* Form Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 3,
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
            Create New Question
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Fill in the details below to create a new question
          </Typography>
        </Box>

        {/* Form Body */}
        <Box sx={{ p: 4 }}>
          {/* Basic Info Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 6, height: 24, bgcolor: '#667eea', borderRadius: 1 }} />
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="sub-lbl">Subject</InputLabel>
                  <Select
                    labelId="sub-lbl" 
                    label="Subject" 
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    }}
                  >
                    {['All','Understanding of Holy Quran','Mathematics','English','Philosophy', 'Philosophy 1','Computer','Computer Science','IT','Physics','Chemistry',
                      'General Knowledge','Ethics','General Science','Islamiat','Medical','Education','Business Administration','Commerce','Research/ORIC','Finance','Examinations','Civil','Electrical Engineering','Security','QEC'].map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="lvl-lbl">Difficulty Level</InputLabel>
                  <Select
                    labelId="lvl-lbl" 
                    label="Difficulty Level" 
                    value={level}
                    onChange={e => setLevel(Number(e.target.value))}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    }}
                  >
                    <MenuItem value={1}>Easy (Level 1)</MenuItem>
                    <MenuItem value={2}>Medium (Level 2)</MenuItem>
                    <MenuItem value={3}>Difficult (Level 3)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="session-lbl">Session (Optional)</InputLabel>
                  <Select
                    labelId="session-lbl" 
                    label="Session (Optional)" 
                    value={sessionId}
                    onChange={e => setSessionId(e.target.value)}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {sessions.map(s => (
                      <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Question Content Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 6, height: 24, bgcolor: '#667eea', borderRadius: 1 }} />
              Question Content
            </Typography>
            <Box sx={{ 
              p: 3, 
              bgcolor: '#f8f9fa', 
              borderRadius: 2, 
              border: '2px dashed #dee2e6',
              '&:hover': {
                borderColor: '#667eea',
                bgcolor: '#f0f4ff',
              },
              transition: 'all 0.3s ease',
            }}>
              <CustomEditor key={`q-${resetCounter}`} value={question} onChange={setQuestion} />
            </Box>
          </Box>

          {/* Sub-Questions Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 24, bgcolor: '#667eea', borderRadius: 1 }} />
                Sub-Questions {subQuestions.length > 0 && `(${subQuestions.length})`}
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => setSubQuestionModalOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                + Add Sub Question
              </Button>
            </Box>
            {subQuestions.length > 0 && (
              <Box sx={{ 
                p: 2, 
                bgcolor: '#e3f2fd', 
                borderRadius: 2,
                border: '1px solid #90caf9',
              }}>
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                  {subQuestions.length} sub question{subQuestions.length > 1 ? 's' : ''} added.
                  <Button 
                    variant="text" 
                    size="small"
                    onClick={() => setViewSubQuestionsModalOpen(true)}
                    sx={{ ml: 1, fontWeight: 600 }}
                  >
                    View All
                  </Button>
                </Typography>
              </Box>
            )}
          </Box>

          {/* Answer Options Section (only if no sub-questions) */}
          {!hasSubQuestions && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 24, bgcolor: '#667eea', borderRadius: 1 }} />
                Answer Options
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
                Select the correct answer by clicking the radio button
              </Typography>
              <RadioGroup
                value={correctOptionIndex}
                onChange={e => setCorrectOptionIndex(Number(e.target.value))}
              >
                <Grid container spacing={2}>
                  {options.map((opt, idx) => (
                    <Grid item xs={12} key={idx}>
                      <Box sx={{
                        p: 2.5,
                        bgcolor: correctOptionIndex === idx ? '#e8f5e9' : '#fafafa',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: correctOptionIndex === idx ? '#4caf50' : '#e0e0e0',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: correctOptionIndex === idx ? '#4caf50' : '#667eea',
                          transform: 'translateX(4px)',
                        },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <FormControlLabel 
                            control={<MuiRadio />} 
                            value={idx} 
                            label=""
                            sx={{ m: 0, mr: 2 }}
                          />
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: correctOptionIndex === idx ? '#4caf50' : '#9e9e9e',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                          }}>
                            {String.fromCharCode(65 + idx)}
                          </Box>
                          <Typography variant="subtitle2" sx={{ ml: 2, fontWeight: 700, color: '#2c3e50' }}>
                            Option {idx + 1}
                          </Typography>
                        </Box>
                        <Box sx={{ pl: 7 }}>
                          <CustomEditor
                            key={`opt-${idx}-${resetCounter}`}
                            value={opt}
                            onChange={val => {
                              const newOpts = [...options]; 
                              newOpts[idx] = val; 
                              setOptions(newOpts);
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 3, borderTop: '2px solid #e0e0e0' }}>
            <Button 
              variant="outlined"
              onClick={() => {
                setQuestion('');
                setOptions(['', '', '', '']);
                setCorrectOptionIndex(0);
                setLevel(1);
                setSubQuestions([]);
                setResetCounter(c => c + 1);
              }}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.2,
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                },
              }}
            >
              Reset Form
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveDraft}
              sx={{ 
                py: 1.2, 
                px: 5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s ease',
              }} 
            >
              Save as Draft
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* --------  SUB-QUESTION MODAL (add in new form) -------------------- */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={subQuestionModalOpen}
        onClose={() => {
          setSubQuestionModalOpen(false);
          setSubQuestionTemp({ question: '', options: ['', '', '', ''], correctOptionIndex: 0 });
        }}
        fullWidth maxWidth="md"
      >
        <DialogTitle>Add Sub Question</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>Sub-question Text</Typography>
          <CustomEditor
            key={`sub-q-${subResetCounter}`}
            value={subQuestionTemp.question}
            onChange={val => setSubQuestionTemp(p => ({ ...p, question: val }))}
          />

          <Typography sx={{ mt: 2 }}>Options / Correct Selection</Typography>
          {subQuestionTemp.options.map((opt, idx) => (
            <Box key={idx} sx={{
              mt: 1, display: 'flex', alignItems: 'center',
              bgcolor: '#fffff2', p: 1, borderRadius: 1,
            }}>
              <FormControlLabel
                control={<MuiRadio
                  checked={subQuestionTemp.correctOptionIndex === idx}
                  onChange={() => setSubQuestionTemp(p => ({ ...p, correctOptionIndex: idx }))}
                />}
                label={`Option ${idx + 1}`}
              />
              <CustomEditor
                key={`sub-opt-${idx}-${subResetCounter}`}
                value={opt}
                onChange={val => {
                  const newOpts = [...subQuestionTemp.options];
                  newOpts[idx] = val;
                  setSubQuestionTemp(p => ({ ...p, options: newOpts }));
                }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubQuestionModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTempSubQuestion}>Save Sub Question</Button>
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ----  VIEW ADDED SUB-QUESTIONS MODAL (read-only)  ----------------- */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={viewSubQuestionsModalOpen}
        onClose={() => setViewSubQuestionsModalOpen(false)}
        fullWidth maxWidth="md"
      >
        <DialogTitle>Sub Questions</DialogTitle>
        <DialogContent dividers>
          {subQuestions.length === 0 ? (
            <Typography>No sub questions added.</Typography>
          ) : (
            subQuestions.map((sq, idx) => (
              <Box key={idx} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 2 }}>
                <Typography variant="subtitle1">Sub Question {idx + 1}</Typography>
                <MathPreview html={sq.question} />
                {sq.options.map((opt, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center',
                    color: opt === sq.correctAnswer ? 'green' : 'inherit',
                  }}>
                    {opt === sq.correctAnswer && <CheckCircleOutline sx={{ mr: 1 }} />}
                    <MathPreview html={opt} />
                  </Box>
                ))}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSubQuestionsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ----------------  VIEW / EDIT MAIN QUESTION DIALOG  --------------- */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Dialog open={viewOpen} onClose={handleCloseView} fullWidth maxWidth="lg">
        {viewedQuestion && (
          <>
            {/* Dialog Header */}
            <Box sx={{
              background: modalEditMode 
                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              p: 3,
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                {modalEditMode ? 'Edit Draft Question' : 'View Question'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {modalEditMode ? 'Make changes to your draft question' : 'Review question details'}
              </Typography>
            </Box>

            <DialogContent dividers sx={{ p: 4, bgcolor: '#fafafa' }}>
              {/* ═══ EDIT-MODE ════════════════════════════════════════════════ */}
              {modalEditMode ? (
                <>
                  {/* Basic Info */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 6, height: 24, bgcolor: '#f5576c', borderRadius: 1 }} />
                      Basic Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Subject</InputLabel>
                          <Select
                            value={viewedQuestion.subject}
                            label="Subject"
                            onChange={e => setViewedQuestion(p => ({ ...p, subject: e.target.value }))}
                            sx={{ 
                              borderRadius: 2,
                              bgcolor: 'white',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                              },
                            }}
                          >
                            {['All','Understanding of Holy Quran','Mathematics','English','Philosophy', 'Philosophy 1','Computer','Computer Science','IT','Physics','Chemistry',
                              'General Knowledge','General Science','Ethics','Islamiat','Medical','Education','Business Administration','Commerce','Research/ORIC','Finance','Examinations','Civil','Electrical Engineering','Security','QEC'].map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Difficulty Level</InputLabel>
                          <Select
                            value={viewedQuestion.level}
                            label="Difficulty Level"
                            onChange={e => setViewedQuestion(p => ({ ...p, level: Number(e.target.value) }))}
                            sx={{ 
                              borderRadius: 2,
                              bgcolor: 'white',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                              },
                            }}
                          >
                            <MenuItem value={1}>Easy (Level 1)</MenuItem>
                            <MenuItem value={2}>Medium (Level 2)</MenuItem>
                            <MenuItem value={3}>Difficult (Level 3)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Question Content */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 6, height: 24, bgcolor: '#f5576c', borderRadius: 1 }} />
                      Question Content
                    </Typography>
                    <Box sx={{ 
                      p: 3, 
                      bgcolor: 'white', 
                      borderRadius: 2, 
                      border: '2px solid #e0e0e0',
                    }}>
                      <CustomEditor
                        key={`edit-q-${resetCounter}`}
                        value={editQuestionText}
                        onChange={setEditQuestionText}
                      />
                    </Box>
                  </Box>

                  {/* Parent Options (if no sub-questions) */}
                  {(!editSubQuestions || editSubQuestions.length === 0) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 24, bgcolor: '#f5576c', borderRadius: 1 }} />
                        Answer Options
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
                        Select the correct answer by clicking the radio button
                      </Typography>
                      <RadioGroup
                        value={viewedQuestion.options.findIndex(opt => opt === viewedQuestion.correctAnswer)}
                        onChange={e => {
                          const idx = Number(e.target.value);
                          setViewedQuestion(p => ({ ...p, correctAnswer: p.options[idx] }));
                        }}
                      >
                        <Grid container spacing={2}>
                          {viewedQuestion.options.map((opt, idx) => (
                            <Grid item xs={12} key={idx}>
                              <Box sx={{
                                p: 2.5,
                                bgcolor: viewedQuestion.correctAnswer === opt ? '#e8f5e9' : 'white',
                                borderRadius: 2,
                                border: '2px solid',
                                borderColor: viewedQuestion.correctAnswer === opt ? '#4caf50' : '#e0e0e0',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: viewedQuestion.correctAnswer === opt ? '#4caf50' : '#f5576c',
                                  transform: 'translateX(4px)',
                                },
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                  <FormControlLabel 
                                    control={<MuiRadio />} 
                                    value={idx} 
                                    label=""
                                    sx={{ m: 0, mr: 2 }}
                                  />
                                  <Box sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: viewedQuestion.correctAnswer === opt ? '#4caf50' : '#9e9e9e',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                  }}>
                                    {String.fromCharCode(65 + idx)}
                                  </Box>
                                  <Typography variant="subtitle2" sx={{ ml: 2, fontWeight: 700, color: '#2c3e50' }}>
                                    Option {idx + 1}
                                  </Typography>
                                </Box>
                                <Box sx={{ pl: 7 }}>
                                  <CustomEditor
                                    key={`edit-opt-${idx}-${resetCounter}`}
                                    value={opt}
                                    onChange={val => {
                                      const newOpts = [...viewedQuestion.options];
                                      const oldOpt = newOpts[idx];
                                      newOpts[idx] = val;
                                      setViewedQuestion(p => ({ 
                                        ...p, 
                                        options: newOpts,
                                        correctAnswer: p.correctAnswer === oldOpt ? val : p.correctAnswer
                                      }));
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </RadioGroup>
                    </Box>
                  )}

                  {/* Sub-Questions Section */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 24, bgcolor: '#f5576c', borderRadius: 1 }} />
                        Sub-Questions {editSubQuestions.length > 0 && `(${editSubQuestions.length})`}
                      </Typography>
                      <Button 
                        variant="contained"
                        onClick={() => {
                          setEditSubModalOpen(true);
                          setSubQuestionTempEdit({ question: '', options: ['', '', '', ''], correctOptionIndex: 0 });
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          borderRadius: 2,
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        + Add Sub Question
                      </Button>
                    </Box>
                    
                    {editSubQuestions.length === 0 ? (
                      <Box sx={{ 
                        p: 3, 
                        bgcolor: 'white', 
                        borderRadius: 2,
                        border: '2px dashed #e0e0e0',
                        textAlign: 'center',
                      }}>
                        <Typography variant="body2" sx={{ color: '#7f8c8d', fontStyle: 'italic' }}>
                          No sub questions added yet.
                        </Typography>
                      </Box>
                    ) : editSubQuestions.map((sq, idx) => (
                      <Box key={idx} sx={{ 
                        p: 3, 
                        mb: 2,
                        bgcolor: 'white',
                        borderRadius: 2, 
                        border: '2px solid #e0e0e0',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#11998e',
                          boxShadow: '0 4px 12px rgba(17, 153, 142, 0.15)',
                        },
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: '#11998e',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                            }}>
                              {idx + 1}
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                              Sub Question {idx + 1}
                            </Typography>
                          </Box>
                          <Button
                            size="small" 
                            color="error" 
                            variant="outlined"
                            onClick={() => setEditSubQuestions(prev => prev.filter((_, j) => j !== idx))}
                            sx={{ borderRadius: 2, fontWeight: 600 }}
                          >
                            Delete
                          </Button>
                        </Box>
                        
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                          <CustomEditor
                            key={`edit-sq-${idx}-${resetCounter}`}
                            value={sq.question}
                            onChange={val => {
                              const us = [...editSubQuestions];
                              us[idx].question = val;
                              setEditSubQuestions(us);
                            }}
                          />
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
                          Answer Options
                        </Typography>
                        <RadioGroup
                          value={sq.options.findIndex(opt => opt === sq.correctAnswer)}
                          onChange={e => {
                            const us = [...editSubQuestions];
                            const i = Number(e.target.value);
                            us[idx].correctAnswer = us[idx].options[i];
                            setEditSubQuestions(us);
                          }}
                        >
                          <Grid container spacing={1.5}>
                            {sq.options.map((opt, i) => (
                              <Grid item xs={12} key={i}>
                                <Box sx={{
                                  p: 2,
                                  bgcolor: sq.correctAnswer === opt ? '#e8f5e9' : '#fafafa',
                                  borderRadius: 1.5,
                                  border: '1px solid',
                                  borderColor: sq.correctAnswer === opt ? '#4caf50' : '#e0e0e0',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: sq.correctAnswer === opt ? '#4caf50' : '#11998e',
                                  },
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <FormControlLabel 
                                      control={<MuiRadio />} 
                                      value={i} 
                                      label=""
                                      sx={{ m: 0, mr: 1.5 }}
                                    />
                                    <Box sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: '50%',
                                      bgcolor: sq.correctAnswer === opt ? '#4caf50' : '#9e9e9e',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 700,
                                      fontSize: '0.9rem',
                                    }}>
                                      {String.fromCharCode(65 + i)}
                                    </Box>
                                  </Box>
                                  <Box sx={{ pl: 5.5 }}>
                                    <CustomEditor
                                      key={`edit-sq-opt-${i}-${resetCounter}`}
                                      value={opt}
                                      onChange={val => {
                                        const us = [...editSubQuestions];
                                        const oldOpt = us[idx].options[i];
                                        us[idx].options[i] = val;
                                        if (us[idx].correctAnswer === oldOpt) {
                                          us[idx].correctAnswer = val;
                                        }
                                        setEditSubQuestions(us);
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
              /* ═══ VIEW-MODE ════════════════════════════════════════════════ */
                <>
                  {/* Info Cards */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2.5, 
                        bgcolor: 'white', 
                        borderRadius: 2,
                        border: '2px solid #e0e0e0',
                        textAlign: 'center',
                      }}>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                          Subject
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', mt: 0.5 }}>
                          {viewedQuestion.subject}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2.5, 
                        bgcolor: 'white', 
                        borderRadius: 2,
                        border: '2px solid #e0e0e0',
                        textAlign: 'center',
                      }}>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                          Difficulty Level
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', mt: 0.5 }}>
                          {viewedQuestion.level === 1 ? 'Easy' : viewedQuestion.level === 2 ? 'Medium' : 'Difficult'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2.5, 
                        bgcolor: 'white', 
                        borderRadius: 2,
                        border: '2px solid #e0e0e0',
                        textAlign: 'center',
                      }}>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                          Sub Questions
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', mt: 0.5 }}>
                          {viewedQuestion.subQuestions?.length || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Question Content */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
                      Question
                    </Typography>
                    <Box sx={{ 
                      p: 3, 
                      bgcolor: 'white', 
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                    }}>
                      <MathPreview html={viewedQuestion.question} />
                    </Box>
                  </Box>

                  {/* Parent Options (if no sub-questions) */}
                  {(!viewedQuestion.subQuestions || viewedQuestion.subQuestions.length === 0) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
                        Answer Options
                      </Typography>
                      <Grid container spacing={2}>
                        {viewedQuestion.options.map((opt, i) => (
                          <Grid item xs={12} key={i}>
                            <Box sx={{
                              p: 2.5,
                              bgcolor: opt === viewedQuestion.correctAnswer ? '#e8f5e9' : 'white',
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: opt === viewedQuestion.correctAnswer ? '#4caf50' : '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}>
                              <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: opt === viewedQuestion.correctAnswer ? '#4caf50' : '#9e9e9e',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: '1.2rem',
                                flexShrink: 0,
                              }}>
                                {String.fromCharCode(65 + i)}
                              </Box>
                              {opt === viewedQuestion.correctAnswer && (
                                <CheckCircleOutline sx={{ color: '#4caf50', fontSize: 28, flexShrink: 0 }} />
                              )}
                              <Box sx={{ flex: 1 }}>
                                <MathPreview html={opt} />
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Sub-Questions (View Mode) */}
                  {viewedQuestion.subQuestions?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2c3e50' }}>
                        Sub Questions ({viewedQuestion.subQuestions.length})
                      </Typography>
                      {viewedQuestion.subQuestions.map((sq, idx) => (
                        <Box key={idx} sx={{ 
                          p: 3, 
                          mb: 2,
                          bgcolor: 'white',
                          borderRadius: 2, 
                          border: '2px solid #e0e0e0',
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: '#11998e',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                            }}>
                              {idx + 1}
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                              Sub Question {idx + 1}
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
                            <MathPreview html={sq.question} />
                          </Box>
                          <Grid container spacing={1.5}>
                            {sq.options.map((opt, i) => (
                              <Grid item xs={12} key={i}>
                                <Box sx={{
                                  p: 2,
                                  bgcolor: opt === sq.correctAnswer ? '#e8f5e9' : '#fafafa',
                                  borderRadius: 1.5,
                                  border: '1px solid',
                                  borderColor: opt === sq.correctAnswer ? '#4caf50' : '#e0e0e0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                }}>
                                  <Box sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: opt === sq.correctAnswer ? '#4caf50' : '#9e9e9e',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    flexShrink: 0,
                                  }}>
                                    {String.fromCharCode(65 + i)}
                                  </Box>
                                  {opt === sq.correctAnswer && (
                                    <CheckCircleOutline sx={{ color: '#4caf50', fontSize: 20, flexShrink: 0 }} />
                                  )}
                                  <Box sx={{ flex: 1 }}>
                                    <MathPreview html={opt} />
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </DialogContent>
          </>
        )}

        <DialogActions sx={{ p: 2.5, bgcolor: '#fafafa', borderTop: '2px solid #e0e0e0' }}>
          {modalEditMode ? (
            <>
              <Button 
                onClick={() => setModalEditMode(false)}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 600,
                }}
              >
                Cancel Edit
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSaveModalEdits}
                sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: 2,
                  px: 4,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e07fea 0%, #e4455a 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(245, 87, 108, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={startEditInModal}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }}
              >
                Edit
              </Button>
              {viewedQuestion?.status === 'review_needed'
                ? <Button 
                    color="secondary" 
                    onClick={handleResubmitModal}
                    sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
                  >
                    Resubmit
                  </Button>
                : <Button 
                    color="error" 
                    onClick={handleDeleteInModal}
                    sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
                  >
                    Delete
                  </Button>
              }
              <Button 
                onClick={handleCloseView}
                variant="contained"
                sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
              >
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* -------------  ADD SUB-QUESTION MODAL inside EDIT dialog ---------- */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Dialog open={editSubModalOpen} onClose={() => setEditSubModalOpen(false)} fullWidth maxWidth="md">
        <Box sx={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          p: 3,
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
            Add New Sub Question
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Create a sub-question with multiple choice options
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 4, bgcolor: '#fafafa' }}>
          {/* Sub-question Text */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
              Sub-Question Text
            </Typography>
            <Box sx={{ 
              p: 3, 
              bgcolor: 'white', 
              borderRadius: 2, 
              border: '2px solid #e0e0e0',
            }}>
              <CustomEditor
                key={`edit-sq-tmp-${editSubResetCounter}`}
                value={subQuestionTempEdit.question}
                onChange={val => setSubQuestionTempEdit(p => ({ ...p, question: val }))}
              />
            </Box>
          </Box>

          {/* Options */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#2c3e50' }}>
              Answer Options
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
              Select the correct answer by clicking the radio button
            </Typography>
            <Grid container spacing={2}>
              {subQuestionTempEdit.options.map((opt, idx) => (
                <Grid item xs={12} key={idx}>
                  <Box sx={{
                    p: 2.5,
                    bgcolor: subQuestionTempEdit.correctOptionIndex === idx ? '#e8f5e9' : 'white',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: subQuestionTempEdit.correctOptionIndex === idx ? '#4caf50' : '#e0e0e0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: subQuestionTempEdit.correctOptionIndex === idx ? '#4caf50' : '#11998e',
                      transform: 'translateX(4px)',
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <FormControlLabel
                        control={<MuiRadio
                          checked={subQuestionTempEdit.correctOptionIndex === idx}
                          onChange={() => setSubQuestionTempEdit(p => ({ ...p, correctOptionIndex: idx }))}
                        />}
                        label=""
                        sx={{ m: 0, mr: 2 }}
                      />
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: subQuestionTempEdit.correctOptionIndex === idx ? '#4caf50' : '#9e9e9e',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}>
                        {String.fromCharCode(65 + idx)}
                      </Box>
                      <Typography variant="subtitle2" sx={{ ml: 2, fontWeight: 700, color: '#2c3e50' }}>
                        Option {idx + 1}
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 7 }}>
                      <CustomEditor
                        key={`edit-sq-opt-tmp-${idx}-${editSubResetCounter}`}
                        value={opt}
                        onChange={val => {
                          const newOpts = [...subQuestionTempEdit.options];
                          newOpts[idx] = val;
                          setSubQuestionTempEdit(p => ({ ...p, options: newOpts }));
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#fafafa', borderTop: '2px solid #e0e0e0' }}>
          <Button 
            onClick={() => setEditSubModalOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddSubQuestionEdit}
            sx={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Save Sub Question
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default QuestionBank;
